import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Filter, Grid, List, Star, RefreshCw, DollarSign, Gavel, X, Folder, Heart, Plus, Tag, Clock, ArrowDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { client } from '../api/client';
import { supabase } from '../lib/supabase';

export const Collection = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'cards' | 'binders' | 'wishlists'>('cards');
  const [cards, setCards] = useState<any[]>([]);
  const [binders, setBinders] = useState<any[]>([]);
  const [wishlists, setWishlists] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  // Modals
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [newItemName, setNewItemName] = useState('');

  // Sell Modal
  const [sellModalOpen, setSellModalOpen] = useState(false);
  const [selectedCard, setSelectedCard] = useState<any>(null);
  const [sellType, setSellType] = useState<'fixed' | 'auction'>('fixed');
  const [price, setPrice] = useState('');
  const [auctionDuration, setAuctionDuration] = useState('7'); // days

  // Pull to refresh state
  const [pullDistance, setPullDistance] = useState(0);
  const [isPulling, setIsPulling] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const startY = useRef(0);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (containerRef.current && containerRef.current.scrollTop === 0) {
      startY.current = e.touches[0].clientY;
      setIsPulling(true);
    }
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isPulling) return;
    const currentY = e.touches[0].clientY;
    const diff = currentY - startY.current;
    if (diff > 0 && diff < 150) {
      setPullDistance(diff);
    }
  }, [isPulling]);

  const handleTouchEnd = useCallback(() => {
    if (pullDistance > 80) {
      setRefreshing(true);
      fetchData();
    }
    setPullDistance(0);
    setIsPulling(false);
  }, [pullDistance]);

  const fetchData = async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const session = await client.auth.getSession();
      if (!session.data?.user) throw new Error("Please log in to view your collection.");
      const user = session.data.user;

      if (activeTab === 'cards') {
        const { data, error } = await supabase
          .from('cards')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });

        if (error) throw error;
        setCards(data || []);

      } else if (activeTab === 'binders') {
        // Fetch binders and count cards
        const { data, error } = await supabase
          .from('binders')
          .select('*, binder_cards(count)')
          .eq('user_id', user.id);

        if (error) throw error;

        // Map to expected format
        const formatted = data?.map((b: any) => ({
          ...b,
          count: b.binder_cards?.[0]?.count || 0
        })) || [];
        setBinders(formatted);

      } else {
        const { data, error } = await supabase
          .from('wishlists')
          .select('*, wishlist_items(count)')
          .eq('user_id', user.id);

        if (error) throw error;

        const formatted = data?.map((w: any) => ({
          ...w,
          count: w.wishlist_items?.[0]?.count || 0
        })) || [];
        setWishlists(formatted);
      }
    } catch (e: any) {
      console.error("Fetch error", e);
      setFetchError(e?.message || 'Unable to load your collection data.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const handleCreate = async () => {
    if (!newItemName) return;
    try {
      const session = await client.auth.getSession();
      if (!session.data?.user) throw new Error("No user");
      const user = session.data.user;

      const table = activeTab === 'binders' ? 'binders' : 'wishlists';

      const { error } = await supabase
        .from(table)
        .insert({
          user_id: user.id,
          name: newItemName,
          description: ''
        });

      if (error) throw error;

      setCreateModalOpen(false);
      setNewItemName('');
      fetchData();
    } catch (e) {
      console.error("Create error", e);
    }
  };

  const openSellModal = (card: any) => {
    setSelectedCard(card);
    setSellType('fixed');
    setPrice(card.price ? card.price.toString().replace('$', '') : '');
    setSellModalOpen(true);
  };

  const handleListForSale = async () => {
    if (!selectedCard || !price) return;

    try {
      const session = await client.auth.getSession();
      if (!session.data?.user) throw new Error("No user");
      const user = session.data.user;

      const priceNum = parseFloat(price);

      if (sellType === 'fixed') {
        // Update card status to 'for_sale' and update price
        const { error } = await supabase
          .from('cards')
          .update({
            status: 'for_sale',
            price: priceNum
          })
          .eq('id', selectedCard.id)
          .eq('user_id', user.id);

        if (error) throw error;

      } else {
        // Create auction and update card status
        const endTime = new Date();
        endTime.setDate(endTime.getDate() + parseInt(auctionDuration));

        // 1. Create Auction
        const { data: auction, error: auctionError } = await supabase
          .from('auctions')
          .insert({
            card_id: selectedCard.id,
            start_price: priceNum,
            current_bid: priceNum,
            end_time: endTime.toISOString(),
            status: 'active'
          })
          .select()
          .single();

        if (auctionError) throw auctionError;

        // 2. Update Card Status
        const { error: cardError } = await supabase
          .from('cards')
          .update({ status: 'auction' })
          .eq('id', selectedCard.id);

        if (cardError) throw cardError;
      }
      setSellModalOpen(false);
      fetchData(); // Refresh to show updated status
    } catch (e) {
      console.error("Listing error", e);
    }
  };

  return (
    <div
      ref={containerRef}
      className="min-h-screen bg-obsidian relative overflow-auto"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Pull to refresh indicator */}
      <AnimatePresence>
        {(pullDistance > 0 || refreshing) && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="absolute top-16 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2 px-4 py-2 bg-copper-500/20 backdrop-blur-xl rounded-full border border-copper-400/30"
          >
            <motion.div
              animate={{ rotate: refreshing ? 360 : pullDistance * 3 }}
              transition={{ duration: refreshing ? 1 : 0, repeat: refreshing ? Infinity : 0, ease: 'linear' }}
            >
              <ArrowDown className={`w-4 h-4 text-copper-400 ${pullDistance > 80 ? 'rotate-180' : ''}`} />
            </motion.div>
            <span className="text-copper-400 text-xs font-medium">
              {refreshing ? 'Refreshing...' : pullDistance > 80 ? 'Release to refresh' : 'Pull to refresh'}
            </span>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Background atmosphere */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-gradient-radial from-copper-500/5 to-transparent rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
      </div>

      <div className="relative z-10 p-5 pt-16 pb-32 space-y-6">
        <header className="sticky top-14 z-20 bg-obsidian/90 backdrop-blur-xl py-3 -mx-5 px-5 border-b border-white/[0.04]">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h1 className="text-2xl font-display font-semibold text-cream-50">My Collection</h1>
              <p className="text-cream-200/40 text-xs mt-0.5">Manage your cards</p>
            </div>
            <button onClick={handleRefresh} aria-label="Refresh collection" className={`p-2.5 card-premium rounded-xl hover:bg-white/5 transition-colors ${refreshing ? 'animate-spin' : ''}`}>
              <RefreshCw className="w-4 h-4 text-cream-200/60" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex p-1 bg-white/[0.03] rounded-xl mb-4 border border-white/[0.04]">
            {(['cards', 'binders', 'wishlists'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`flex-1 py-2.5 text-xs font-semibold rounded-lg capitalize transition-all duration-300 ${activeTab === tab
                  ? 'bg-copper-500/20 text-copper-400 border border-copper-400/30'
                  : 'text-cream-200/40 hover:text-cream-200/70 border border-transparent'
                  }`}
              >
                {tab}
              </button>
            ))}
          </div>

          {activeTab === 'cards' && (
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-cream-200/40" />
              <input
                type="text"
                placeholder="Search your cards..."
                className="w-full bg-white/[0.03] border border-white/[0.06] rounded-xl py-3 pl-11 pr-4 text-sm text-cream-100 placeholder-cream-200/30 focus:outline-none focus:border-copper-400/50 focus:bg-white/[0.05] transition-all"
              />
            </div>
          )}
        </header>

        {fetchError && (
          <div className="card-premium border border-danger/30 bg-danger/10 px-4 py-3 rounded-xl">
            <p className="text-danger text-sm font-medium">{fetchError}</p>
            <p className="text-cream-200/60 text-xs mt-1">If this says Unauthorized, your session token is missing/expired and saves will not appear until you sign in again.</p>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="flex gap-1.5">
              {[0, 1, 2].map(i => (
                <div key={i} className={`w-2 h-8 bg-copper-400/30 rounded-full animate-pulse ${i === 1 ? 'delay-150' : i === 2 ? 'delay-300' : ''}`} />
              ))}
            </div>
          </div>
        ) : (
          <>
            {activeTab === 'cards' && (
              <div className="grid grid-cols-2 gap-4">
                <motion.button
                  onClick={() => window.location.href = '/catalog'}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="aspect-[3/4] card-premium flex flex-col items-center justify-center border-2 border-dashed border-white/10 hover:border-copper-400/40 transition-colors group"
                >
                  <div className="w-14 h-14 rounded-2xl bg-copper-500/10 flex items-center justify-center mb-3 group-hover:bg-copper-500/20 transition-colors">
                    <Plus className="w-6 h-6 text-copper-400" />
                  </div>
                  <span className="text-sm font-medium text-cream-200/60 group-hover:text-cream-100 transition-colors">Add Card</span>
                </motion.button>

                {cards.map((card, i) => (
                  <motion.div
                    key={card.id || i}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05 }}
                    whileHover={{ y: -4 }}
                    className="card-premium overflow-hidden group cursor-pointer"
                  >
                    <div className="aspect-[3/4] relative overflow-hidden">
                      <img
                        src={(card.image_url || card.imageUrl) && (card.image_url || card.imageUrl)!.startsWith('http') ? (card.image_url || card.imageUrl)! : 'https://placehold.co/300x400/1C1C1F/666?text=No+Image'}
                        alt={card.name || 'Card image'}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          if (!target.src.includes('placehold.co')) {
                            target.src = 'https://placehold.co/300x400/1C1C1F/666?text=No+Image';
                          }
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-obsidian/80 via-transparent to-transparent" />
                      <div className="absolute top-2 right-2 bg-obsidian/60 backdrop-blur-md px-2.5 py-1 rounded-lg text-[10px] font-semibold text-cream-100 border border-white/10 flex items-center gap-1">
                        <Star className="w-3 h-3 text-copper-400 fill-copper-400" />
                        {card.grade || 'Raw'}
                      </div>
                      {/* Status Badge */}
                      {card.status === 'for_sale' && (
                        <div className="absolute top-2 left-2 bg-success/90 text-cream-50 text-[10px] font-bold px-2 py-1 rounded-lg">
                          FOR SALE
                        </div>
                      )}
                      {card.status === 'auction' && (
                        <div className="absolute top-2 left-2 bg-copper-500/90 text-cream-50 text-[10px] font-bold px-2 py-1 rounded-lg">
                          AUCTION
                        </div>
                      )}
                    </div>
                    <div className="p-3">
                      <h3 className="font-semibold text-sm truncate text-cream-100">{card.name}</h3>
                      <p className="text-[10px] text-cream-200/40 truncate">{card.set_name}</p>
                      <div className="flex justify-between items-center mt-2">
                        <span className="text-sm font-semibold text-success">
                          {typeof card.price === 'number' ? `$${card.price.toFixed(2)}` : card.price}
                        </span>
                        {card.status === 'collection' && (
                          <button
                            onClick={() => openSellModal(card)}
                            aria-label="Sell card"
                            className="p-1.5 bg-white/5 rounded-lg hover:bg-copper-500/20 hover:text-copper-400 transition-colors"
                          >
                            <DollarSign size={14} />
                          </button>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}

            {activeTab === 'binders' && (
              <div className="grid grid-cols-2 gap-4">
                <motion.button
                  onClick={() => setCreateModalOpen(true)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="aspect-[3/4] card-premium flex flex-col items-center justify-center border-2 border-dashed border-white/10 hover:border-copper-400/40 transition-colors group"
                >
                  <div className="w-14 h-14 rounded-2xl bg-sage-500/10 flex items-center justify-center mb-3 group-hover:bg-sage-500/20 transition-colors">
                    <Plus className="w-6 h-6 text-sage-400" />
                  </div>
                  <span className="text-sm font-medium text-cream-200/60 group-hover:text-cream-100 transition-colors">New Binder</span>
                </motion.button>

                {binders.map((binder, i) => (
                  <motion.div
                    key={binder.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05 }}
                    whileHover={{ y: -4 }}
                    onClick={() => navigate(`/binders/${binder.id}`)}
                    className="aspect-[3/4] card-premium p-5 flex flex-col justify-between cursor-pointer group"
                  >
                    <div className="w-12 h-12 rounded-2xl bg-sage-500/15 flex items-center justify-center">
                      <Folder className="w-6 h-6 text-sage-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-cream-100 group-hover:text-copper-400 transition-colors">{binder.name}</h3>
                      <p className="text-xs text-cream-200/40">{binder.count || 0} Cards</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}

            {activeTab === 'wishlists' && (
              <div className="grid grid-cols-2 gap-4">
                <motion.button
                  onClick={() => setCreateModalOpen(true)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="aspect-[3/4] card-premium flex flex-col items-center justify-center border-2 border-dashed border-white/10 hover:border-copper-400/40 transition-colors group"
                >
                  <div className="w-14 h-14 rounded-2xl bg-danger/10 flex items-center justify-center mb-3 group-hover:bg-danger/20 transition-colors">
                    <Plus className="w-6 h-6 text-danger" />
                  </div>
                  <span className="text-sm font-medium text-cream-200/60 group-hover:text-cream-100 transition-colors">New Wishlist</span>
                </motion.button>

                {wishlists.map((list, i) => (
                  <motion.div
                    key={list.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05 }}
                    whileHover={{ y: -4 }}
                    onClick={() => navigate(`/wishlists/${list.id}`)}
                    className="aspect-[3/4] card-premium p-5 flex flex-col justify-between cursor-pointer group"
                  >
                    <div className="w-12 h-12 rounded-2xl bg-danger/15 flex items-center justify-center">
                      <Heart className="w-6 h-6 text-danger" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-cream-100 group-hover:text-copper-400 transition-colors">{list.name}</h3>
                      <p className="text-xs text-cream-200/40">{list.count || 0} Items</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Create Modal */}
      <AnimatePresence>
        {createModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-carbon border border-white/[0.06] rounded-2xl p-6 w-full max-w-sm shadow-elevated"
            >
              <div className="flex justify-between items-center mb-5">
                <h3 className="text-xl font-display font-semibold text-cream-50">Create New {activeTab === 'binders' ? 'Binder' : 'Wishlist'}</h3>
                <button onClick={() => setCreateModalOpen(false)} aria-label="Close modal" className="p-1.5 rounded-lg hover:bg-white/5 text-cream-200/50 hover:text-cream-100 transition-colors">
                  <X size={18} />
                </button>
              </div>
              <input
                type="text"
                value={newItemName}
                onChange={(e) => setNewItemName(e.target.value)}
                placeholder="Name (e.g., Rookie Cards)"
                className="w-full bg-white/[0.03] border border-white/[0.08] rounded-xl p-4 text-cream-100 placeholder-cream-200/30 mb-5 focus:outline-none focus:border-copper-400/50 transition-colors"
              />
              <button
                onClick={handleCreate}
                className="w-full py-3.5 bg-copper-500 text-cream-50 rounded-xl font-semibold hover:bg-copper-400 transition-colors"
              >
                Create
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Sell Modal */}
      <AnimatePresence>
        {sellModalOpen && selectedCard && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-carbon border border-white/[0.06] rounded-2xl p-6 w-full max-w-sm shadow-elevated"
            >
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-display font-semibold text-cream-50">List for Sale</h3>
                <button onClick={() => setSellModalOpen(false)} aria-label="Close modal" className="p-1.5 rounded-lg hover:bg-white/5 text-cream-200/50 hover:text-cream-100 transition-colors">
                  <X size={18} />
                </button>
              </div>

              {/* Tabs */}
              <div className="flex p-1 bg-white/[0.03] rounded-xl mb-6 border border-white/[0.04]">
                <button
                  onClick={() => setSellType('fixed')}
                  className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all ${sellType === 'fixed' ? 'bg-copper-500/20 text-copper-400 border border-copper-400/30' : 'text-cream-200/40 border border-transparent'}`}
                >
                  Buy It Now
                </button>
                <button
                  onClick={() => setSellType('auction')}
                  className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all ${sellType === 'auction' ? 'bg-sage-500/20 text-sage-400 border border-sage-400/30' : 'text-cream-200/40 border border-transparent'}`}
                >
                  Auction
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-xs text-cream-200/50 mb-1.5 block font-medium">Price ($)</label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cream-200/40" />
                    <input
                      type="number"
                      value={price}
                      onChange={(e) => setPrice(e.target.value)}
                      className="w-full bg-white/[0.03] border border-white/[0.08] rounded-xl py-3 pl-10 pr-4 text-cream-100 placeholder-cream-200/30 focus:outline-none focus:border-copper-400/50 transition-colors"
                      placeholder={sellType === 'fixed' ? "Selling Price" : "Starting Bid"}
                    />
                  </div>
                </div>

                {sellType === 'auction' && (
                  <div>
                    <label className="text-xs text-cream-200/50 mb-1.5 block font-medium">Duration</label>
                    <div className="relative">
                      <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cream-200/40" />
                      <select
                        value={auctionDuration}
                        onChange={(e) => setAuctionDuration(e.target.value)}
                        aria-label="Auction Duration"
                        title="Auction Duration"
                        className="w-full bg-white/[0.03] border border-white/[0.08] rounded-xl py-3 pl-10 pr-4 text-cream-100 focus:outline-none focus:border-copper-400/50 appearance-none"
                      >
                        <option value="1">1 Day</option>
                        <option value="3">3 Days</option>
                        <option value="5">5 Days</option>
                        <option value="7">7 Days</option>
                      </select>
                    </div>
                  </div>
                )}

                <button
                  onClick={handleListForSale}
                  className={`w-full py-3 rounded-xl font-bold text-cream-50 transition-colors ${sellType === 'fixed' ? 'bg-copper-500 hover:bg-copper-400' : 'bg-sage-600 hover:bg-sage-500'}`}
                >
                  {sellType === 'fixed' ? 'List Item' : 'Start Auction'}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};
